import { useState, useEffect } from "react";
import api from "./api";
import { installTenantTimezone, setTenantTimezone } from "./tenantTimezone";

installTenantTimezone();

const DEFAULT_SETTINGS = {
  phone_ddi: "+55",
  phone_mask: "(##) #####-####",
  currency_code: "BRL",
  currency_symbol: "R$",
  timezone: "America/Sao_Paulo",
  date_format: "DD/MM/YYYY",
  number_format: "pt-BR",
};

let cachedSettings = null;

export function getTenantSettings() {
  return cachedSettings || DEFAULT_SETTINGS;
}

/** Descarta o cache (chamar após salvar Configurações > Gerais). */
export function invalidateTenantSettings() {
  cachedSettings = null;
}

/** Carrega e cacheia as configurações do tenant (usado no boot pós-login). */
export async function loadTenantSettings() {
  if (cachedSettings) return cachedSettings;
  try {
    const { data } = await api.get("/settings/general");
    if (data && Object.keys(data).length > 0) {
      cachedSettings = { ...DEFAULT_SETTINGS, ...data };
      setTenantTimezone(cachedSettings.timezone);
    }
  } catch {
    /* sem configuração ainda: segue com o padrão */
  }
  return cachedSettings || DEFAULT_SETTINGS;
}

export function useTenantSettings() {
  const [settings, setSettings] = useState(cachedSettings || DEFAULT_SETTINGS);

  useEffect(() => {
    if (cachedSettings) return;
    api.get("/settings/general").then(({ data }) => {
      if (data && Object.keys(data).length > 0) {
        const merged = { ...DEFAULT_SETTINGS, ...data };
        cachedSettings = merged;
        setTenantTimezone(merged.timezone);
        setSettings(merged);
      }
    }).catch(() => {});
    // Dependências intencionalmente vazias: `DEFAULT_SETTINGS` e `api` são
    // imports de módulo (estáveis), e `cachedSettings` é uma variável de
    // módulo lida apenas via guard de early-return (não precisa estar em deps).
     
  }, []);

  return settings;
}

/**
 * Format a date/datetime string using the tenant's date_format and timezone settings.
 * Supported tokens: DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD, DD.MM.YYYY.
 * When withTime is true, appends "HH:mm" (2-digit) using the tenant timezone.
 */
export function formatDateTenant(iso, { withTime = true, dateFormat, timezone } = {}) {
  if (!iso) return "Não informado";
  const s = cachedSettings || DEFAULT_SETTINGS;
  const fmt = dateFormat || s.date_format || "DD/MM/YYYY";
  const tz = timezone || s.timezone || "America/Sao_Paulo";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "Não informado";
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const DD = parts.day, MM = parts.month, YYYY = parts.year;
    const HH = parts.hour === "24" ? "00" : parts.hour;
    const mm = parts.minute;
    let datePart;
    switch (fmt) {
      case "MM/DD/YYYY": datePart = `${MM}/${DD}/${YYYY}`; break;
      case "YYYY-MM-DD": datePart = `${YYYY}-${MM}-${DD}`; break;
      case "DD.MM.YYYY": datePart = `${DD}.${MM}.${YYYY}`; break;
      case "DD/MM/YYYY":
      default: datePart = `${DD}/${MM}/${YYYY}`; break;
    }
    return withTime ? `${datePart} ${HH}:${mm}` : datePart;
  } catch {
    return "Não informado";
  }
}

/**
 * ISO (UTC) → valor de campo "YYYY-MM-DDTHH:mm" no FUSO DO TENANT.
 *
 * Sem isso o campo mostra a hora em UTC: um abastecimento salvo às 14:30 de
 * São Paulo aparecia como 17:30 ao reabrir pra editar.
 */
export function isoToTenantInput(iso, { timezone } = {}) {
  if (!iso) return "";
  const tz = timezone || (cachedSettings || DEFAULT_SETTINGS).timezone || "America/Sao_Paulo";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((acc, x) => { acc[x.type] = x.value; return acc; }, {});
  const HH = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${HH}:${p.minute}`;
}

/** Diferença (ms) entre a hora de parede do fuso e o UTC, naquele instante. */
function tenantOffsetMs(date, tz) {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(date).reduce((acc, x) => { acc[x.type] = x.value; return acc; }, {});
  const HH = p.hour === "24" ? "00" : p.hour;
  const comoUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +HH, +p.minute, +p.second);
  return comoUtc - date.getTime();
}

/**
 * Valor de campo "YYYY-MM-DDTHH:mm" (hora de parede do TENANT) → ISO UTC.
 *
 * O que o usuário digita é a hora do fuso da empresa, não a do computador
 * dele — quem opera de outro estado/país salvava com o horário errado.
 */
export function tenantInputToIso(local, { timezone } = {}) {
  if (!local) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/.exec(local);
  if (!m) return "";
  const tz = timezone || (cachedSettings || DEFAULT_SETTINGS).timezone || "America/Sao_Paulo";
  const [, Y, Mo, D, H, Mi] = m.map(Number);
  const paredeUtc = Date.UTC(Y, Mo - 1, D, H, Mi);
  // Duas passadas resolvem as bordas de horário de verão.
  let utc = paredeUtc;
  for (let i = 0; i < 2; i++) {
    utc = paredeUtc - tenantOffsetMs(new Date(utc), tz);
  }
  return new Date(utc).toISOString();
}

export function applyPhoneMask(value, mask) {  const digits = value.replace(/\D/g, "");
  let result = "";
  let digitIndex = 0;
  for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
    if (mask[i] === "#") {
      result += digits[digitIndex];
      digitIndex++;
    } else {
      result += mask[i];
    }
  }
  return result;
}

/**
 * Format a number for display based on tenant number_format setting.
 * "pt-BR" → 1.234,56 (comma decimal)
 * "en-US" → 1,234.56 (dot decimal)
 */
export function formatNumberDisplay(value, numberFormat = "pt-BR") {
  if (value === "" || value === null || value === undefined) return "";
  const num = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
  if (isNaN(num)) return "";
  return num.toLocaleString(numberFormat, { minimumFractionDigits: 0, maximumFractionDigits: 10 });
}

/**
 * Apply number mask to input value (allows only digits + the correct decimal separator).
 * "pt-BR" → comma as decimal separator
 * "en-US" → dot as decimal separator
 */
export function maskNumberInput(value, numberFormat = "pt-BR") {
  const decimalSep = numberFormat === "pt-BR" ? "," : ".";
  // Remove everything except digits and the correct decimal separator
  let cleaned = "";
  let hasDecimal = false;
  for (const ch of value) {
    if (ch >= "0" && ch <= "9") {
      cleaned += ch;
    } else if ((ch === "," || ch === ".") && !hasDecimal) {
      cleaned += decimalSep;
      hasDecimal = true;
    }
  }
  return cleaned;
}

/**
 * Parse a display-formatted number back to a float-safe string (dot decimal).
 * "9,5" (pt-BR) → "9.5"
 */
export function parseNumberToFloat(value, numberFormat = "pt-BR") {
  if (!value) return "";
  if (numberFormat === "pt-BR") {
    return value.replace(/\./g, "").replace(",", ".");
  }
  return value.replace(/,/g, "");
}

/**
 * Format a money value to display string using the tenant currency config.
 * Input: numeric value (e.g. 1234.56), symbol (e.g. "R$"), code (e.g. "BRL"), numberFormat.
 * Output: "R$ 1.234,56"
 */
export function formatMoneyTenant(value, { currency_symbol = "R$", currency_code = "BRL", number_format = "pt-BR" } = {}) {
  const num = typeof value === "string" ? parseFloat(value.replace(",", ".")) : value;
  if (num === "" || num === null || num === undefined || isNaN(num)) return "";
  const formatted = num.toLocaleString(number_format, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${currency_symbol} ${formatted}`;
}

/**
 * Money input mask using the tenant number_format (keeps raw digits → cents).
 * Returns the display string. Store raw digits count in a separate state if needed.
 */
export function maskMoneyInput(value, { currency_symbol = "R$", number_format = "pt-BR" } = {}) {
  const digits = (value || "").toString().replace(/\D/g, "");
  if (!digits) return "";
  const num = parseInt(digits, 10) / 100;
  const formatted = num.toLocaleString(number_format, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${currency_symbol} ${formatted}`;
}

/**
 * Parse a money display string back to a float (for persistence).
 * "R$ 1.234,56" → 1234.56
 */
export function parseMoneyToFloat(value, { number_format = "pt-BR" } = {}) {
  if (!value) return 0;
  const digits = value.toString().replace(/\D/g, "");
  if (!digits) return 0;
  return parseInt(digits, 10) / 100;
}
